import {
  type TeamMemberWithUserDto,
  TeamsApi,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useState } from 'react';

import type { MemberRosterRow } from '@/core/lib/profile-template-member-values-csv';

function memberToRow(m: TeamMemberWithUserDto): MemberRosterRow | null {
  const userId = m.userId || m.user?.id;
  if (!userId) return null;
  const first = m.user?.firstName ?? '';
  const last = m.user?.lastName ?? '';
  const displayName = `${first} ${last}`.trim() || m.user?.email || userId;
  return {
    userId,
    displayName,
    email: m.user?.email ?? undefined,
  };
}

export function useTemplateMemberRoster(
  workspaceId: string | undefined,
  teamIds: string[],
) {
  const [roster, setRoster] = useState<MemberRosterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId || teamIds.length === 0) {
      setRoster([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        teamIds.map((teamId) =>
          TeamsApi.listMembers({ path: { workspaceId, teamId } }),
        ),
      );
      const byUser = new Map<string, MemberRosterRow>();
      for (const res of results) {
        const members = (res.data ?? []) as TeamMemberWithUserDto[];
        for (const m of members) {
          const row = memberToRow(m);
          if (row) byUser.set(row.userId, row);
        }
      }
      setRoster(
        [...byUser.values()].sort((a, b) =>
          a.displayName.localeCompare(b.displayName, undefined, {
            sensitivity: 'base',
          }),
        ),
      );
    } catch (err) {
      console.error(err);
      setError('loadFailed');
      setRoster([]);
    } finally {
      setLoading(false);
    }
  }, [teamIds, workspaceId]);

  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, [load]);

  return { roster, loading, error, reload: load };
}
